import { LogEntry, LogTransport } from '../types';
import { mapLogLevel } from '@/utils/severity';

// We'll define the interface for Application Insights to avoid importing the full SDK
interface ApplicationInsights {
  trackTrace(trace: {
    message: string;
    severityLevel?: number;
    properties?: { [key: string]: any };
  }): void;
  flush(): void;
}

export class ApplicationInsightsTransport implements LogTransport {
  private appInsights: ApplicationInsights | null = null;
  private initialized = false;
  private pendingLogs: LogEntry[] = [];

  constructor(private connectionString?: string) {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Dynamic import to avoid bundling in development
      const { ApplicationInsights } = await import(
        '@microsoft/applicationinsights-web'
      );

      const appInsights = new ApplicationInsights({
        config: {
          connectionString:
            this.connectionString ||
            import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
          enableAutoRouteTracking: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          samplingPercentage: 100, // We handle sampling in Logger class
          maxBatchSizeInBytes: 1024 * 1024, // 1MB
          maxBatchInterval: 10000, // 10 seconds
          disableExceptionTracking: false,
          disableTelemetry: false,
          loggingLevelTelemetry: 0, // Disable internal logging
          extensions: [],
          extensionConfig: {},
        },
      });

      appInsights.loadAppInsights();
      this.appInsights = appInsights;
      this.initialized = true;

      // Set common properties
      appInsights.addTelemetryInitializer(envelope => {
        envelope.tags = envelope.tags || {};
        envelope.tags['ai.cloud.role'] = 'spa-ui';
        envelope.tags['ai.cloud.roleInstance'] = window.location.hostname;
      });

      // Process any pending logs
      for (const entry of this.pendingLogs) {
        await this.log(entry);
      }
      this.pendingLogs = [];
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  async log(entry: LogEntry): Promise<void> {
    if (!this.initialized || !this.appInsights) {
      // Queue logs until initialized
      this.pendingLogs.push(entry);
      return;
    }

    try {
      this.appInsights.trackTrace({
        message: entry.message,
        severityLevel: mapLogLevel(entry.level),
        properties: {
          ...entry.meta,
          level: entry.level,
          correlationId: entry.correlationId,
          module: entry.module,
          sessionId: entry.sessionId,
          timestamp: entry.timestamp,
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
      });
    } catch (error) {
      console.error('Failed to track trace in Application Insights:', error);
    }
  }

  // mapLogLevel method removed - now using centralized utility from @/utils/severity

  async flush(): Promise<void> {
    if (!this.initialized || !this.appInsights) {
      return;
    }

    try {
      this.appInsights.flush();
    } catch (error) {
      console.error('Failed to flush Application Insights:', error);
    }
  }
}
